// src/pages/Shop.js
import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { getProducts, getCategories } from "../utils/firestoreService";

const SORT_OPTIONS = [
  { value: "newest", label: "Más recientes" },
  { value: "price-asc", label: "Menor precio" },
  { value: "price-desc", label: "Mayor precio" },
  { value: "rating", label: "Mejor calificados" }
];

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const activeCat = searchParams.get("cat") || "";

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const prods = await getProducts(activeCat || null);
      setProducts(prods);
    } finally {
      setLoading(false);
    }
  }, [activeCat]);

  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => { getCategories().then(setCategories); }, []);

  const filtered = products
    .filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.shortDesc?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sort === "price-asc") return a.price - b.price;
      if (sort === "price-desc") return b.price - a.price;
      if (sort === "rating") return (b.rating || 0) - (a.rating || 0);
      return 0;
    });

  const setCategory = (slug) => {
    if (slug) setSearchParams({ cat: slug });
    else setSearchParams({});
    setSidebarOpen(false);
  };

  return (
    <div className="shop-page">
      <div className="shop-header">
        <h1>Tienda</h1>
        <p>Descubre nuestras piezas únicas de decoración y souvenirs impresos en 3D</p>
      </div>

      <div className="shop-layout">
        {/* SIDEBAR */}
        <aside className={`shop-sidebar ${sidebarOpen ? "open" : ""}`}>
          <div className="sidebar-header">
            <h3>Filtros</h3>
            <button className="close-sidebar" onClick={() => setSidebarOpen(false)}>✕</button>
          </div>

          <div className="filter-group">
            <h4>Categorías</h4>
            <ul className="cat-list">
              <li>
                <button
                  className={!activeCat ? "active" : ""}
                  onClick={() => setCategory("")}
                >Todos</button>
              </li>
              {categories.map(cat => (
                <li key={cat.id}>
                  <button
                    className={activeCat === cat.slug ? "active" : ""}
                    onClick={() => setCategory(cat.slug)}
                  >{cat.name}</button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* MAIN */}
        <div className="shop-main">
          <div className="shop-controls">
            <button className="filter-toggle" onClick={() => setSidebarOpen(true)}>
              ☰ Filtros
            </button>
            <div className="search-wrap">
              <input
                type="search"
                placeholder="Buscar productos..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <select value={sort} onChange={e => setSort(e.target.value)} className="sort-select">
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div className="results-info">
            {activeCat && <span className="active-filter">Categoría: {activeCat} <button onClick={() => setCategory("")}>✕</button></span>}
            <span>{filtered.length} producto{filtered.length !== 1 ? "s" : ""}</span>
          </div>

          {loading ? (
            <div className="skeleton-grid">
              {[1,2,3,4,5,6].map(i => <div key={i} className="skeleton-card" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🔍</div>
              <h3>No encontramos productos</h3>
              <p>Intenta con otros términos o explora todas las categorías.</p>
              <button onClick={() => { setSearch(""); setCategory(""); }} className="btn-primary">Ver todos</button>
            </div>
          ) : (
            <div className="products-grid">
              {filtered.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
